import React from 'react';
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Zap, Code, FileText, PlayCircle, ArrowRight, Star, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { getLargeIconForType } from "@/lib/iconMapping";
import { integrationService } from "@/lib/api/services";
import { useApi } from "@/hooks/useApi";
import { PageSkeleton, GenericError } from "@/components/common";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Helper function to convert title to slug
const slugify = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

// Mock workflows for demonstration - these could be fetched from API in the future
const mockWorkflows = [
  {
    id: "1",
    title: "Customer Support Chatbot",
    description: "Automated customer support system that handles common inquiries and escalates complex issues.",
    author: "Sarah Chen",
    rating: 4.8,
    uses: 1240,
    image: "/placeholder.svg"
  },
  {
    id: "2", 
    title: "Content Moderation Pipeline",
    description: "AI-powered content moderation that detects inappropriate content in real-time.",
    author: "Mike Rodriguez",
    rating: 4.6,
    uses: 890,
    image: "/placeholder.svg"
  },
  {
    id: "3",
    title: "Email Auto-Reply System",
    description: "Intelligent email responses that understand context and provide helpful replies.",
    author: "Emma Thompson", 
    rating: 4.7,
    uses: 650,
    image: "/placeholder.svg"
  }
];

export default function NodeDetailPage() {
  const { nodeId } = useParams();
  
  // Fetch all integrations and find the one that matches the slug
  const integrationsResult = useApi(integrationService.list, [], { showErrorToast: true });
  
  const node = React.useMemo(() => {
    if (!integrationsResult.data || !nodeId) return null;
    return integrationsResult.data.data.find(integration =>
      integration.id === nodeId
    );
  }, [integrationsResult.data, nodeId]);

  if (integrationsResult.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50 dark:from-slate-900 dark:to-violet-900">
        <Header currentPage="integrations" variant="gallery" />
        <PageSkeleton />
        <Footer />
      </div>
    );
  }

  if (integrationsResult.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50 dark:from-slate-900 dark:to-violet-900">
        <Header currentPage="integrations" variant="gallery" />
        <div className="container mx-auto px-4 py-16">
          <GenericError onRetry={() => integrationsResult.refetch()} />
        </div>
        <Footer />
      </div>
    );
  }

  if (!node) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50 dark:from-slate-900 dark:to-violet-900">
        <Header currentPage="integrations" variant="gallery" />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Node not found</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-8">The integration you're looking for doesn't exist.</p>
          <Link to="/integrations">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Integrations
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50 dark:from-slate-900 dark:to-violet-900">
      <Header currentPage="integrations" variant="gallery" />
      
      {/* Hero Section */}
      <div className="bg-white dark:bg-slate-800 border-b">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <Link to="/integrations">
              <Button variant="ghost" className="mb-6 text-purple-600 hover:text-purple-800">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Integrations
              </Button>
            </Link>

            <div className="flex flex-col md:flex-row items-start md:justify-between mb-8">
              <div className="flex items-start gap-6">
                <div className="flex items-center justify-center">
                  {getLargeIconForType(node.iconType)}
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-3">{node.title}</h1>
                  <p className="text-xl text-slate-600 dark:text-slate-300 mb-4 max-w-2xl">{node.description}</p>
                  
                  <div className="flex items-center gap-3">
                    {node.featured && (
                      <Badge className="bg-purple-600 hover:bg-purple-700">
                        <Zap className="h-3 w-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    <Badge variant="outline">{node.category}</Badge>
                    <Badge variant="outline">v{node.version}</Badge>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                {node.documentation && (
                  <Button variant="outline" asChild>
                    <a href={node.documentation} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Documentation
                    </a>
                  </Button>
                )}
                <Button className="bg-purple-600 hover:bg-purple-700">
                  <Copy className="h-4 w-4 mr-2" />
                  Add to Flow
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Inputs & Outputs Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <Code className="h-8 w-8 mr-3 text-purple-600" />
              Inputs & Outputs
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Inputs */}
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Inputs</CardTitle>
                  <CardDescription>Data this node accepts</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {node.configuration.inputs.map((input, index) => (
                    <div key={index} className="border-b border-slate-200 dark:border-slate-700 last:border-b-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-slate-900 dark:text-white">{input.name}</h4>
                        <Badge variant="outline" className="text-xs">
                          {input.type}
                        </Badge>
                        {input.required && (
                          <Badge variant="destructive" className="text-xs">
                            Required
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 text-sm mb-2">{input.description}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Outputs */}
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Outputs</CardTitle>
                  <CardDescription>Data this node produces</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {node.configuration.outputs.map((output, index) => (
                    <div key={index} className="border-b border-slate-200 dark:border-slate-700 last:border-b-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-slate-900 dark:text-white">{output.name}</h4>
                        <Badge variant="outline" className="text-xs">
                          {output.type}
                        </Badge>
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 text-sm mb-2">{output.description}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator />

          {/* Documentation Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <FileText className="h-8 w-8 mr-3 text-purple-600" />
              Documentation
            </h2>
            
            <Card className="bg-white dark:bg-slate-800">
              <CardContent className="p-8">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  <h3>Overview</h3>
                  <p>
                    The {node.title} node provides advanced AI-powered text processing capabilities. It leverages state-of-the-art 
                    language models to understand, analyze, and respond to natural language input with high accuracy and contextual awareness.
                  </p>
                  
                  <h3>Configuration</h3>
                  <p>
                    This node can be configured with various parameters to customize its behavior:
                  </p>
                  <ul>
                    <li><strong>Model Selection:</strong> Choose from different AI models based on your needs for speed vs. accuracy</li>
                    <li><strong>Temperature Control:</strong> Adjust the creativity and randomness of responses</li>
                    <li><strong>Context Management:</strong> Configure how the node handles conversation history and context</li>
                  </ul>

                  <h3>Best Practices</h3>
                  <p>
                    For optimal performance:
                  </p>
                  <ul>
                    <li>Provide clear, specific instructions in your prompts</li>
                    <li>Use appropriate temperature settings (lower for factual tasks, higher for creative tasks)</li>
                    <li>Monitor token usage to optimize costs</li>
                    <li>Implement proper error handling for API failures</li>
                  </ul>

                  <h3>Rate Limits & Pricing</h3>
                  <p>
                    This integration is subject to the API provider's rate limits and pricing. Check the official documentation 
                    for current limits and costs. Consider implementing caching strategies for frequently requested content.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* Workflows Using This Node */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <PlayCircle className="h-8 w-8 mr-3 text-purple-600" />
              Workflows Using This Node
            </h2>
            
            <div className="grid md:grid-cols-1 gap-6 mb-8">
              {mockWorkflows.map((workflow) => (
                <Card key={workflow.id} className="bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      <img
                        src={workflow.image}
                        alt={`${workflow.title} preview`}
                        className="w-24 h-16 object-cover rounded-lg bg-slate-100 dark:bg-slate-700"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{workflow.title}</h3>
                        <p className="text-slate-600 dark:text-slate-300 mb-3">{workflow.description}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                          <span>by {workflow.author}</span>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span>{workflow.rating}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span>1.2k uses</span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        View Workflow
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Link to="/gallery/workflows">
                <Button variant="outline" size="lg" className="bg-purple-600 text-white hover:bg-purple-700">
                  <PlayCircle className="h-5 w-5 mr-2" />
                  View All Workflows
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </div>
          </section>

        </div>
      </div>
      
      <Footer />
    </div>
  );
}
